import { type V2, angleTo, dist, smoothstep } from '../../core/math';
import { Rng } from '../../core/rng';
import { pointInPolygon } from '../../sim/navGrid';
import { polylineDistance } from '../../zones/build';
import {
  type Area,
  disc,
  fillBuildings,
  gate,
  isOpen,
  lampsAlong,
  lifeNodes,
  paint,
  place,
  placeFacing,
  pointAt,
  ringOf,
  rowAlong,
  sideOffset,
  stations,
  wall,
} from '../../zones/townKit';
import type { ZoneLayout } from '../../zones/types';
import { ZoneBuilder } from '../../zones/ZoneBuilder';

const WATER = -1.3;
/** Shoreline of the north-west harbour (water lies north-west of it). */
const SHORE: V2[] = [
  { x: -96, z: -20 },
  { x: -78, z: -22 },
  { x: -64, z: -28 },
  { x: -56, z: -38 },
  { x: -52, z: -52 },
  { x: -48, z: -66 },
  { x: -45, z: -80 },
  { x: -43, z: -96 },
];
const HARBOUR: V2[] = [...SHORE, { x: -96, z: -96 }];
/** Piers: start on the quay, heading out to sea. */
const PIER_A = { a: { x: -54, z: -37 }, dir: { x: -0.78, z: -0.63 }, len: 26, hw: 2.6 };
const PIER_B = { a: { x: -49, z: -60 }, dir: { x: -0.94, z: -0.34 }, len: 17, hw: 2.1 };

const pierEnd = (p: typeof PIER_A): V2 => ({ x: p.a.x + p.dir.x * p.len, z: p.a.z + p.dir.z * p.len });
const pierAt = (p: typeof PIER_A, t: number): V2 => ({ x: p.a.x + p.dir.x * t, z: p.a.z + p.dir.z * t });
const nearPier = (p: V2, pad: number): boolean => [PIER_A, PIER_B].some((pr) => polylineDistance(p, [pr.a, pierEnd(pr)]) < pr.hw + pad);

/**
 * HAVENREACH — the walled hub city north of Emberwild Vale.
 *
 * Layout grammar: the south gate opens onto a processional avenue that runs
 * north to the fountain plaza (the service ring), past the raised Waystone
 * Dais and up a ramp to the Warden's Keep. Districts branch off the plaza on
 * the diagonals: harbour (NW), Gate Square with bounty board, rift frame and
 * arena (NE), forge (SW), residences (SE); the market street runs east to the
 * Sunscar gate. The camera looks north, so landmarks sit up-screen at the end
 * of each street and buildings keep back from the south side of open spaces.
 */
export function buildHavenreach(): ZoneLayout {
  const rng = new Rng('havenreach');
  const b = new ZoneBuilder(
    {
      id: 'havenreach', name: 'Havenreach', subtitle: 'City of the Lantern Watch', kind: 'town', biome: 'town',
      levelRange: [1, 30], music: 'town', width: 184, depth: 184, seed: 2101,
      ground: ['cobble', 'tiles', 'grass', 'dirt'], groundScale: [4, 3, 4, 5],
      hills: 0.5, hillScale: 0.02, waterLevel: WATER, ambient: 'motes', mapTint: 0x6e665a,
    },
    rng,
  );
  const C: V2 = { x: 0, z: 0 };
  const at = (a: number, r: number): V2 => ({ x: C.x + Math.sin(a) * r, z: C.z + Math.cos(a) * r });
  const PI = Math.PI;

  // ---------------------------------------------------------------- terrain
  // Harbour basin carved below the water line, sparing the piers.
  const pierMask = (p: V2, pr: typeof PIER_A): number => {
    const d = polylineDistance(p, [pr.a, pierEnd(pr)]);
    return 1 - smoothstep(pr.hw - 0.3, pr.hw + 0.4, d);
  };
  b.addHeight((x, z) => {
    if (x > -40 || z > -16) return 0;
    const p = { x, z };
    if (!pointInPolygon(p, HARBOUR)) return 0;
    const k = smoothstep(0, 3.5, polylineDistance(p, SHORE));
    const keep = Math.max(pierMask(p, PIER_A), pierMask(p, PIER_B));
    return -5.5 * k * (1 - keep);
  });
  // Grass outside the walls and in the gardens; dirt on the quay.
  paint(b, 2, (x, z) => (Math.max(Math.abs(x), Math.abs(z)) > 81.5 ? 1 : 0), 6);
  const gardens: [V2, number][] = [
    [{ x: 56, z: 34 }, 7],
    [{ x: 30, z: 58 }, 7],
    [{ x: 64, z: 64 }, 6],
    [{ x: -30, z: -48 }, 7],
    [{ x: 24, z: -64 }, 6],
    [{ x: -64, z: 70 }, 6],
    [{ x: -22, z: 64 }, 5],
  ];
  for (const [g, r] of gardens) paint(b, 2, disc(g, r, 2), 5);
  const inGarden = (p: V2, pad = 0) => gardens.some(([g, r]) => dist(p, g) < r + pad);
  paint(b, 3, (x, z) => {
    const d = polylineDistance({ x, z }, SHORE);
    return d < 7 && !pointInPolygon({ x, z }, HARBOUR) ? 1 - smoothstep(4, 7, d) : 0;
  });

  // ---------------------------------------------------------------- streets
  const avenue: V2[] = [{ x: 0, z: 20 }, { x: 0, z: 92 }];
  const keepWay: V2[] = [{ x: 0, z: -20 }, { x: 0, z: -52 }];
  const market: V2[] = [{ x: 20, z: 2 }, { x: 40, z: 6 }, { x: 60, z: 10 }, { x: 92, z: 13 }];
  const gateLane: V2[] = [at((3 * PI) / 4, 20), { x: 28, z: -26 }, { x: 36, z: -33 }];
  const harbourRd: V2[] = [at((-3 * PI) / 4, 20), { x: -28, z: -24 }, { x: -40, z: -30 }, { x: -50, z: -34 }];
  const quay: V2[] = [{ x: -76, z: -16 }, { x: -62, z: -22 }, { x: -52, z: -33 }, { x: -47, z: -49 }, { x: -43.5, z: -63 }, { x: -41, z: -74 }];
  const forgeRd: V2[] = [at(-PI / 4, 20), { x: -30, z: 30 }, { x: -42, z: 42 }];
  const eastRes: V2[] = [at(PI / 4, 20), { x: 30, z: 30 }, { x: 46, z: 46 }, { x: 62, z: 62 }];
  const westRes: V2[] = [{ x: -30, z: 30 }, { x: -30, z: 56 }, { x: -46, z: 70 }];
  const northRes: V2[] = [{ x: -28, z: -24 }, { x: -24, z: -44 }, { x: -12, z: -60 }];
  const streets = [avenue, keepWay, market, gateLane, harbourRd, quay, forgeRd, eastRes, westRes, northRes];
  b.road(avenue, 8, 1, 0);
  b.road(keepWay, 7, 1, 0);
  b.road(market, 7, 1, 0);
  b.road(gateLane, 6, 1, 0);
  b.road(harbourRd, 6, 1, 0);
  b.road(quay, 6, 3, 0);
  b.road(forgeRd, 6, 1, 0);
  b.road(eastRes, 5, 1, 0);
  b.road(westRes, 4.5, 3, 0);
  b.road(northRes, 4.5, 3, 0);

  // Plaza, dais, keep terrace, Gate Square, forge yard.
  b.flat(C, 20, { height: 0, layer: 1, soft: 4 });
  const DAIS: V2 = { x: 0, z: -11 };
  b.flat(DAIS, 4.5, { height: 0.6, layer: 1, soft: 2 });
  const KEEP: V2 = { x: 0, z: -62 };
  b.flat(KEEP, 14, { height: 1.8, layer: 1, soft: 7 });
  const SQUARE: V2 = { x: 40, z: -38 };
  b.flat(SQUARE, 11, { height: 0.3, layer: 1, soft: 3 });
  const FORGE: V2 = { x: -50, z: 50 };
  b.flat(FORGE, 12, { height: 0, layer: 3, soft: 3 });
  b.flat({ x: 38, z: 38 }, 6, { height: 0, layer: 1, soft: 2 });

  // ---------------------------------------------------------------- walls
  b.walkable = { kind: 'poly', pts: [{ x: -81, z: -81 }, { x: 81, z: -81 }, { x: 81, z: 81 }, { x: -81, z: 81 }] };
  const southGate = gate(b, { x: 0, z: 80 }, 0, { towerScale: 1.2 });
  const eastGate = gate(b, { x: 80, z: 12 }, PI / 2, { towerScale: 1.2 });
  wall(b, [{ x: -80, z: -24 }, { x: -80, z: 80 }, { x: 80, z: 80 }, { x: 80, z: -80 }, { x: -46, z: -80 }], { gaps: [southGate, eastGate], towerEvery: 40, openEnds: true, towerScale: 1.1 });
  // Gardens: a couple of trees and flower beds each, then kept free of buildings.
  for (const [g, r] of gardens) {
    const area = { minX: g.x - r, minZ: g.z - r, maxX: g.x + r, maxZ: g.z + r };
    const inG = (p: V2) => dist(p, g) < r - 1;
    b.scatterProps([{ model: 'tree_oak', weight: 2, collider: { r: 0.6 }, scale: [0.8, 1.1] }], 2, area, 5, (p) => inG(p) && isOpen(b, p, 1.2), 1);
    b.scatterProps([{ model: 'bush_flower', weight: 3, scale: [0.7, 1.1], noShadow: true }, { model: 'bush', weight: 1, scale: [0.7, 1], noShadow: true }], 6, area, 1.8, (p) => inG(p) && isOpen(b, p, 0), 0);
    b.reserve(g, r);
  }
  // Harbour towers where the walls meet the sea.
  place(b, 'tower', -80, -19, 0, { scale: 1.3 });
  place(b, 'tower', -41, -80, 0, { scale: 1.3 });
  place(b, 'banner_pole', -5, 76);
  place(b, 'banner_pole', 5, 76);
  place(b, 'banner_pole', 76, 7);
  place(b, 'banner_pole', 76, 17);

  // ---------------------------------------------------------------- plaza (service ring)
  b.spawn('start', 0, 13, PI);
  place(b, 'fountain', C.x, C.z + 3);
  // Waystone Dais: raised platform, rune ring and braziers on the diagonals.
  b.prop('waystone', DAIS.x, DAIS.z, { rot: 0 });
  b.interact({ id: 'hr_waystone_plaza', kind: 'waystone', x: DAIS.x, z: DAIS.z, data: { name: 'Havenreach Plaza' } });
  ringOf(b, 'brazier', DAIS, 5.6, 4, { phase: PI / 4 });
  // Portal statue (the return-song target) behind the dais.
  const statue: V2 = { x: -9, z: -19 };
  placeFacing(b, 'statue_hero', statue, C, { scale: 1.4 });
  b.interact({ id: 'hr_lore_statue', kind: 'lore', x: -8, z: -15.5, data: { lore: 'lantern_bearer_oath' } });
  for (const a of [(7 * PI) / 8, (-7 * PI) / 8]) {
    const p = at(a, 18);
    place(b, 'banner_pole', p.x, p.z);
  }
  // Service NPCs on the ring between the street mouths, facing the fountain.
  const ring = (id: string, a: number, r = 15): void => {
    const p = at(a, r);
    b.npc(id, p.x, p.z, angleTo(p, C));
  };
  ring('gemcutter_ysa', (3 * PI) / 8);
  ring('card_collector_mira', (5 * PI) / 8);
  ring('sigil_scribe_orin', (-5 * PI) / 8);
  ring('keeper_marn', (-3 * PI) / 8);
  b.npc('minstrel_lio', 4.5, 8.5, 0.3);
  // Their shopfronts sit behind them on the plaza rim.
  const shop = (a: number, model: string, tint?: number): void => {
    const p = at(a, 28);
    place(b, model, p.x, p.z, angleTo(p, C), { tint });
  };
  shop((3 * PI) / 8, 'house_a', 0xc8b8a0);
  shop((5 * PI) / 8, 'house_c', 0xb8c4d0);
  shop((-5 * PI) / 8, 'tower', 0xa8a8c0);
  shop((-3 * PI) / 8, 'house_c', 0xc0b090);
  shop(-PI / 2, 'house_b');
  for (const a of [(3 * PI) / 8, (-3 * PI) / 8]) {
    const p = at(a, 22.5);
    place(b, 'crate', p.x + 1.2, p.z, 0.3);
    place(b, 'barrel', p.x - 1.2, p.z + 0.4);
  }
  for (const a of [PI / 8, -PI / 8, (7 * PI) / 8 + 0.2, (-7 * PI) / 8 - 0.2]) {
    const p = at(a, 17.5);
    placeFacing(b, 'bench', p, C);
  }
  for (const a of [PI / 8, -PI / 8, (3 * PI) / 8, (-3 * PI) / 8, (5 * PI) / 8, (-5 * PI) / 8]) {
    const p = at(a, 20.5);
    place(b, 'lamp_post', p.x, p.z, a);
  }

  // ---------------------------------------------------------------- the keep (north landmark)
  // Great hall with a tall donjon behind it: the tallest silhouette in town, at the end of the axis.
  place(b, 'house_c', KEEP.x, KEEP.z - 9, 0, { scale: 2.2, tint: 0xd8d0c0 });
  place(b, 'tower', KEEP.x, KEEP.z - 12, 0, { scale: 2, tint: 0xd8d0c0 });
  place(b, 'tower', KEEP.x - 14.8, KEEP.z - 8, 0, { scale: 1.5 });
  place(b, 'tower', KEEP.x + 14.8, KEEP.z - 8, 0, { scale: 1.5 });
  b.npc('high_warden_aldara', KEEP.x, KEEP.z + 4, 0);
  place(b, 'brazier', KEEP.x - 3.5, KEEP.z + 2);
  place(b, 'brazier', KEEP.x + 3.5, KEEP.z + 2);
  place(b, 'statue_hero', KEEP.x - 6, KEEP.z + 11, 0, { scale: 1.3 });
  place(b, 'statue_hero', KEEP.x + 6, KEEP.z + 11, 0, { scale: 1.3 });
  for (const sx of [-10, 10]) place(b, 'banner_pole', KEEP.x + sx, KEEP.z + 2);
  b.interact({ id: 'hr_vista_keep', kind: 'vista', x: KEEP.x + 10, z: KEEP.z + 7, data: { name: "Warden's Rampart" } });

  // ---------------------------------------------------------------- Gate Square (bounty, rift, arena)
  // Arena: hexagonal walled bowl whose iron gate faces the square.
  const ARENA: V2 = { x: 63, z: -60 };
  const face = angleTo(ARENA, SQUARE);
  const hex: V2[] = [];
  for (let k = 0; k <= 6; k++) {
    const a = face + PI / 6 + (k * PI) / 3;
    hex.push({ x: ARENA.x + Math.sin(a) * 12, z: ARENA.z + Math.cos(a) * 12 });
  }
  const arenaGate: V2 = { x: ARENA.x + Math.sin(face) * 12 * Math.cos(PI / 6), z: ARENA.z + Math.cos(face) * 12 * Math.cos(PI / 6) };
  wall(b, hex, { gaps: [{ p: arenaGate, w: 5 }], towerScale: 0.9, tint: 0xd0b8a0 });
  place(b, 'gate_iron', arenaGate.x, arenaGate.z, face);
  paint(b, 3, disc(ARENA, 9, 2));
  b.reserve(ARENA, 12);
  for (const a of [0, (2 * PI) / 3, (4 * PI) / 3]) place(b, 'banner_pole', ARENA.x + Math.sin(a) * 5, ARENA.z + Math.cos(a) * 5, 0, { reserve: false });
  const rix = { x: arenaGate.x + Math.sin(face) * 3.6, z: arenaGate.z + Math.cos(face) * 3.6 };
  b.npc('arena_herald_rix', rix.x, rix.z, face);
  place(b, 'banner_pole', arenaGate.x + Math.sin(face + 1.2) * 4.5, arenaGate.z + Math.cos(face + 1.2) * 4.5);
  place(b, 'banner_pole', arenaGate.x + Math.sin(face - 1.2) * 4.5, arenaGate.z + Math.cos(face - 1.2) * 4.5);
  // Rift frame on a low dais at the north rim, with the scholar beside it.
  const FRAME: V2 = { x: 36, z: -51 };
  b.flat(FRAME, 3.5, { height: 0.8, layer: 1, soft: 1.5 });
  place(b, 'portal_frame', FRAME.x, FRAME.z, 0, { scale: 1.2 });
  place(b, 'rift_core', FRAME.x, FRAME.z - 0.2, 0, { scale: 0.6, y: 1.2, collider: null });
  place(b, 'brazier', FRAME.x - 5, FRAME.z + 1);
  place(b, 'brazier', FRAME.x + 5, FRAME.z + 1);
  const elowen = { x: FRAME.x + 5.5, z: FRAME.z + 4.5 };
  b.npc('rift_scholar_elowen', elowen.x, elowen.z, angleTo(elowen, SQUARE));
  // Bounty Board on the east rim.
  const board = { x: 51.5, z: -37 };
  placeFacing(b, 'notice_board', board, SQUARE);
  const quill = { x: 49, z: -32.5 };
  b.npc('bounty_master_quill', quill.x, quill.z, angleTo(quill, SQUARE));
  place(b, 'crate', 53.5, -33, 0.4);
  place(b, 'banner_pole', 29, -44);
  place(b, 'banner_pole', 29.5, -31);
  place(b, 'statue_hero', 45, -26.5, PI + 0.6, { scale: 1.1 });
  b.spawn('fromInstance', SQUARE.x - 2, SQUARE.z + 3, PI * 0.75);

  // ---------------------------------------------------------------- market street (east)
  // Stalls line the north side (facing the camera); shops the south side.
  for (const s of stations(market, 7, 8, 36)) {
    const p = sideOffset(s.p, s.angle, 7);
    placeFacing(b, 'market_stall', p, s.p, { tint: rng.pick([0xd8c090, 0xc09080, 0x90a8c0, 0xb8c890]) });
  }
  const tessa = pointAt(market, 12);
  const tessaP = sideOffset(tessa.p, tessa.angle, 4.2);
  b.npc('merchant_tessa', tessaP.x, tessaP.z, angleTo(tessaP, tessa.p));
  place(b, 'barrel', tessaP.x + 2.4, tessaP.z - 0.5);
  place(b, 'crate', tessaP.x - 2.6, tessaP.z - 0.4, 0.2);
  const venn = pointAt(market, 45);
  const exch = sideOffset(venn.p, venn.angle, 12);
  placeFacing(b, 'house_c', exch, venn.p, { scale: 1.25, tint: 0xe0d0b0 });
  const vennP = sideOffset(venn.p, venn.angle, 4.5);
  b.npc('broker_venn', vennP.x, vennP.z, angleTo(vennP, venn.p));
  place(b, 'banner_pole', vennP.x - 4, vennP.z - 1.5);
  place(b, 'banner_pole', vennP.x + 4, vennP.z - 1);
  const kael = pointAt(market, 29);
  const armoury = sideOffset(kael.p, kael.angle, -11);
  placeFacing(b, 'house_b', armoury, kael.p, { scale: 1.15, tint: 0x9aa0a8 });
  const kaelP = sideOffset(kael.p, kael.angle, -4.8);
  b.npc('armorer_kael', kaelP.x, kaelP.z, angleTo(kaelP, kael.p));
  place(b, 'crate', kaelP.x + 2.5, kaelP.z + 1.5, 0.7);
  place(b, 'barrel', kaelP.x - 2.5, kaelP.z + 1.2);
  rowAlong(b, market, { side: -1, setback: 11, spacing: 11, models: ['house_a', 'house_b'], start: 8, end: 56, tints: [0xd8c8b0, 0xc8b8a0, 0xe0d8c8] });
  const cartSpot = pointAt(market, 52);
  const cartP = sideOffset(cartSpot.p, cartSpot.angle, 5.5);
  place(b, 'cart', cartP.x, cartP.z, cartSpot.angle);

  // ---------------------------------------------------------------- harbour (north-west)
  for (const pr of [PIER_A, PIER_B]) {
    const rot = Math.atan2(pr.dir.x, pr.dir.z);
    for (let t = 2; t <= pr.len - 1; t += 4) {
      const p = pierAt(pr, t);
      b.prop('dock_segment', p.x, p.z, { rot });
    }
    const end = pierEnd(pr);
    place(b, 'lamp_post', end.x - pr.dir.z * (pr.hw - 0.4), end.z + pr.dir.x * (pr.hw - 0.4), rot);
  }
  const shipRot = Math.atan2(PIER_A.dir.x, PIER_A.dir.z);
  const shipP = sideOffset(pierAt(PIER_A, 17), shipRot, -6.8);
  // Props sit on the terrain, so floating things are lifted from the harbour floor to the water line.
  const afloat = (p: V2): number => WATER - b.heightAt(p.x, p.z);
  place(b, 'ship_player', shipP.x, shipP.z, shipRot, { y: afloat(shipP) });
  const gangway = pierAt(PIER_A, 17);
  b.portal({ id: 'hr_to_sea', to: 'sea', toSpawn: 'fromTown', x: gangway.x, z: gangway.z, radius: 2.4, label: 'The Gleaming Sea', kind: 'dock', minLevel: 15 });
  const sol = { x: -50.5, z: -38 };
  b.npc('harbormaster_sol', sol.x, sol.z, angleTo(sol, { x: -38, z: -30 }));
  b.spawn('fromSea', -47, -41, PI * 0.75);
  const fish = pierAt(PIER_B, 14);
  b.interact({ id: 'hr_fish_0', kind: 'fish', x: fish.x, z: fish.z, data: { skill: 'fishing' } });
  for (const p of [{ x: -76, z: -44 }, { x: -70, z: -74 }, { x: -86, z: -62 }, { x: -62, z: -86 }]) b.prop('buoy', p.x, p.z, { rot: 0, y: afloat(p) });
  // Warehouses face the water across the quay; crates and carts on the quayside.
  rowAlong(b, quay, { side: -1, setback: 10, spacing: 11, models: ['house_b', 'house_c'], tints: [0x8a7a68, 0x9a8a78, 0x7a8088], start: 4 });
  for (const s of stations(quay, 9, 6)) {
    const p = sideOffset(s.p, s.angle, 4.2);
    if (!isOpen(b, p, 0.6) || nearPier(p, 2.5)) continue;
    const r = rng.next();
    if (r < 0.35) {
      place(b, 'crate', p.x, p.z, rng.range(0, PI));
      place(b, 'crate', p.x + 0.9, p.z + 0.3, rng.range(0, PI), { scale: 0.8 });
    } else if (r < 0.65) place(b, 'barrel', p.x, p.z);
    else if (r < 0.8) place(b, 'cart', p.x, p.z, s.angle);
  }
  lampsAlong(b, quay, 12, 3.8, { stagger: true });

  // ---------------------------------------------------------------- forge district (south-west)
  const borin = { x: -45, z: 45 };
  b.npc('smith_borin', borin.x, borin.z, angleTo(borin, { x: -30, z: 30 }));
  place(b, 'forge_anvil', -48.5, 42.5, 0.6);
  place(b, 'forge_anvil', -52, 47.5, -0.4);
  place(b, 'house_c', -63, 47, PI / 2, { scale: 1.3, tint: 0x6e6258 });
  place(b, 'house_a', -52, 64, PI, { tint: 0x7a6a5a });
  place(b, 'brazier', -56.5, 42);
  place(b, 'brazier', -56.5, 53);
  place(b, 'brazier', -44, 55);
  place(b, 'cart', -40, 56, 0.9);
  for (const p of [{ x: -58, z: 58 }, { x: -59, z: 59.5 }, { x: -60.5, z: 58.2 }]) place(b, 'barrel', p.x, p.z);
  for (const p of [{ x: -42, z: 38.5 }, { x: -43, z: 37.4 }]) place(b, 'crate', p.x, p.z, 0.5);
  place(b, 'rock_b', -60, 38, 0, { scale: 0.6, tint: 0x3a3a3a });
  place(b, 'banner_pole', -38, 44);

  // ---------------------------------------------------------------- avenue & gates
  b.spawn('fromEmberwild', 0, 70, PI);
  b.portal({ id: 'hr_to_emberwild', to: 'emberwild', toSpawn: 'fromTown', x: 0, z: 80, radius: 3, label: 'Emberwild Vale', kind: 'exit' });
  b.spawn('fromSunscar', 70, 11, -PI / 2);
  b.portal({ id: 'hr_to_sunscar', to: 'sunscar', toSpawn: 'fromTown', x: 80, z: 12, radius: 3, label: 'Sunscar Expanse', kind: 'exit', minLevel: 12 });
  place(b, 'signpost', 6.5, 66, 0.4);
  place(b, 'signpost', 66, 17, -1.2);
  lampsAlong(b, avenue, 12, 5.6, { start: 6, end: 58 });
  lampsAlong(b, keepWay, 10, 4.6, { start: 8, end: 26 });
  lampsAlong(b, market, 12, 4.3, { stagger: true, start: 5, end: 58 });
  lampsAlong(b, gateLane, 9, 3.8, { stagger: true, start: 4 });
  lampsAlong(b, harbourRd, 10, 3.8, { stagger: true, start: 5 });
  lampsAlong(b, forgeRd, 10, 3.8, { stagger: true, start: 5 });
  lampsAlong(b, eastRes, 12, 3.4, { stagger: true, start: 6 });
  for (const z of [34, 58]) {
    place(b, 'bench', -5.2, z, PI / 2);
    place(b, 'bench', 5.2, z, -PI / 2);
  }
  rowAlong(b, avenue, { side: 1, setback: 13, spacing: 12, models: ['house_a', 'house_b', 'house_c'], start: 12, end: 50, tints: [0xe0d4c0, 0xd0c0a8, 0xc8d0d8] });
  rowAlong(b, avenue, { side: -1, setback: 13, spacing: 12, models: ['house_a', 'house_b', 'house_c'], start: 12, end: 50, tints: [0xe0d4c0, 0xd0c0a8, 0xc8d0d8] });

  // ---------------------------------------------------------------- residences
  const well: V2 = { x: 40.5, z: 35.5 };
  place(b, 'well', well.x, well.z);
  rowAlong(b, eastRes, { side: 1, setback: 10, spacing: 11, models: ['house_a', 'house_b'], start: 8, tints: [0xe8dcc8, 0xd8c8b0] });
  rowAlong(b, eastRes, { side: -1, setback: 10, spacing: 11, models: ['house_a', 'house_b'], start: 8, tints: [0xe8dcc8, 0xd8c8b0] });
  rowAlong(b, westRes, { side: 1, setback: 9, spacing: 11, models: ['house_a', 'house_b'], start: 4 });
  rowAlong(b, northRes, { side: -1, setback: 9, spacing: 11, models: ['house_a', 'house_b', 'house_c'], start: 4 });
  const interior: Area = { minX: -74, minZ: -74, maxX: 74, maxZ: 74 };
  fillBuildings(b, interior, {
    spacing: 13, jitter: 1.5, models: ['house_a', 'house_a', 'house_b', 'house_b', 'house_c'], streets,
    tints: [0xe8dcc8, 0xd8c8b0, 0xc8d0d8, 0xd0b8a0, 0xe0e0d0],
    accept: (p) => !pointInPolygon(p, HARBOUR) && polylineDistance(p, SHORE) > 12 && !inGarden(p, 5),
  });

  // ---------------------------------------------------------------- collectibles
  lifeNodes(b, 'hr', [
    { kind: 'seed', count: 1, area: { minX: -74, minZ: -76, maxX: -56, maxZ: -40 }, minDist: 10 },
    { kind: 'seed', count: 1, area: { minX: 50, minZ: 52, maxX: 76, maxZ: 76 }, minDist: 10 },
    { kind: 'seed', count: 1, area: { minX: -76, minZ: 56, maxX: -56, maxZ: 76 }, minDist: 10 },
    { kind: 'seed', count: 1, area: { minX: 14, minZ: -76, maxX: 34, maxZ: -56 }, minDist: 10 },
  ]);

  // ---------------------------------------------------------------- greenery & clutter
  b.scatterProps(
    [
      { model: 'barrel', weight: 3, collider: { r: 0.45 } },
      { model: 'crate', weight: 3, collider: { w: 0.9, d: 0.9 } },
      { model: 'cart', weight: 0.6, collider: { w: 2.2, d: 1.4 } },
    ],
    40, interior, 7, (p) => isOpen(b, p, 1.2) && !pointInPolygon(p, HARBOUR) && !inGarden(p), 0.5,
  );
  // Countryside beyond the walls.
  const outside = (p: V2) => Math.max(Math.abs(p.x), Math.abs(p.z)) > 84 && polylineDistance(p, avenue) > 7 && polylineDistance(p, market) > 7 && !pointInPolygon(p, HARBOUR);
  const full = { minX: -92, minZ: -92, maxX: 92, maxZ: 92 };
  b.scatterProps([{ model: 'tree_oak', weight: 3, collider: { r: 0.7 }, scale: [0.9, 1.4] }, { model: 'tree_pine', weight: 1, collider: { r: 0.6 }, scale: [0.9, 1.3] }], 60, full, 5, outside, 0);
  b.scatterProps([{ model: 'bush', weight: 3, scale: [0.8, 1.3], noShadow: true }, { model: 'grass_tuft', weight: 4, scale: [0.8, 1.4], noShadow: true }, { model: 'rock_a', weight: 0.5, scale: [0.5, 0.9] }], 160, full, 2.2, outside, 0);

  return b.build();
}
